import { isNil } from 'lodash';

import {
  START_DATETIME_PARAM,
  START_DATETIME_LTE_PARAM,
} from 'components/Common/VerticalFilterV2/constants';
import {
  MonthDayFullYearDashed,
  FullTime24Hour,
} from 'constants/dateTimeFormats';
import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleJSONResponse,
  handleFileResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

import dayjs from 'services/dayjs';

import { MY_TERRITORY_FILTER } from 'modules/EventSchedulerV2/constants';

const TIMEZONE_KEY = 'User-Timezone';

const queryParamMapping = {
  startDatetime: {
    name: START_DATETIME_PARAM,
    transform: ({ date, time }) =>
      date && time
        ? `${date.format(MonthDayFullYearDashed)} ${time.format(FullTime24Hour)}`
        : undefined,
  },
  endDatetime: {
    name: START_DATETIME_LTE_PARAM,
    transform: ({ date, time }) =>
      date && time
        ? `${date.format(MonthDayFullYearDashed)} ${time.format(FullTime24Hour)}`
        : undefined,
  },
  startDate: {
    name: 'start_date_after',
    transform: value =>
      value ? dayjs(value).format(MonthDayFullYearDashed) : undefined,
  },
  endDate: {
    name: 'start_date_before',
    transform: value =>
      value ? dayjs(value).format(MonthDayFullYearDashed) : undefined,
  },
  format: {
    name: 'attendanceFormat',
    transform: values => values.map(({ id }) => id).join(','),
  },
  type: {
    name: 'eventFormat',
    transform: values => values.map(({ id }) => id).join(','),
  },
  startTime: {
    name: 'start_time_after',
    transform: v => (v ? dayjs(v).format(FullTime24Hour) : undefined),
  },
  endTime: {
    name: 'start_time_before',
    transform: v => (v ? dayjs(v).format(FullTime24Hour) : undefined),
  },
  state: {
    name: 'state',
    transform: values => values.map(v => v?.id).join(','),
  },
  latitude: { name: 'latitude' },
  longitude: { name: 'longitude' },
  radius: { name: 'distance' },
  myTerritoryEvents: {
    name: 'myTerritoryEvents',
    transform: territoryFilter =>
      territoryFilter === MY_TERRITORY_FILTER ? true : undefined,
  },
};

export const transformFiltersToQueryParams = filters =>
  Object.entries(filters).reduce((acc, [filterName, filterValue]) => {
    const config = queryParamMapping[filterName];
    if (config && !isNil(filterValue)) {
      const paramName = config.name;
      const transform = config.transform || (value => value);
      const transformedValue = transform(filterValue) || undefined;

      if (!isNil(transformedValue)) {
        acc[paramName] = transformedValue;
      }
    }
    return acc;
  }, {});

export const getRepEventDistricts = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/visit-scheduler/districts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRepEventSchools = ({
  version = 'v1',
  districtUuid,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/visit-scheduler/districts/${districtUuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEvents = ({ version = 'v1', params, timezone }) =>
  client
    .request({
      url: `${version}/event-scheduler/student/events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianEventsCount = ({ version = 'v1', params, timezone }) =>
  client
    .request({
      url: `${version}/event-scheduler/guardian/events/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEventsCount = ({ version = 'v1', params, timezone }) =>
  client
    .request({
      url: `${version}/event-scheduler/student/events/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEventDetail = ({
  version = 'v1',
  params,
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/student/events/${eventId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEventTemplate = ({ timezone, payload, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/event-templates/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventTemplates = ({ timezone, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/event-templates/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTemplateDetails = ({ timezone, id, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/event-templates/${id}/`,
      method: RequestMethod.GET,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEventTemplate = ({
  timezone,
  id,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/event-templates/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventTemplate = ({ timezone, id, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/event-templates/${id}/`,
      method: RequestMethod.DELETE,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEvent = ({ timezone, payload, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEvents = ({ timezone, params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventDetails = ({ timezone, eventId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${eventId}/`,
      method: RequestMethod.GET,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEvent = ({ timezone, id, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEvent = ({ timezone, id, version = 'v1', payload } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/delete-event/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventsByConfirmationStatus = ({
  timezone,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createRecurringSlots = ({
  timezone,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/recurring-slots/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecurringSlots = ({ timezone, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/recurring-slots/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteRecurringSlot = ({ timezone, id, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/recurring-slots/${id}`,
      method: RequestMethod.DELETE,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateRecurringSlot = ({
  timezone,
  id,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/recurring-slots/${id}`,
      method: RequestMethod.PATCH,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgEventApplications = ({
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgEventApplicationCount = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createBlockedDate = ({ timezone, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/blocked-dates/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getBlockedDates = ({ params, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/blocked-dates/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateBlockedDate = ({
  id,
  timezone,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/blocked-dates/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteBlockedDate = ({ id, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/blocked-dates/${id}/`,
      method: RequestMethod.DELETE,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEventApplication = ({
  payload,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventApplication = ({ id, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/${id}/`,
      method: RequestMethod.DELETE,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const approveEventApplication = ({
  id,
  payload,
  timezone,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/${id}/approve/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadStudentEventFile = ({
  version = 'v1',
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/student/events/${eventId}/calendar-event/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const rejectEventApplication = ({
  id,
  payload,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/${id}/reject/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberEventDetails = ({
  version = 'v1',
  hashId,
  timezone,
  params,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/${hashId}/`,
      method: RequestMethod.GET,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberUpcomingOrgEvents = ({
  version = 'v1',
  params,
  timezone,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberUpcomingOrgEventsByDates = ({
  version = 'v1',
  params,
  timezone,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/events-by-dates/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadGuardianEventFile = ({
  version = 'v1',
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/guardian/events/${eventId}/calendar-event/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const downloadK12AdminEventFile = ({
  version = 'v1',
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${eventId}/calendar-event/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const registerMemberEvent = ({
  version = 'v1',
  eventId,
  params,
  payload,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/${eventId}/register/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unregisterMemberEvent = ({
  version = 'v1',
  eventId,
  params,
  payload,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/${eventId}/unregister/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const registerStudentForEvent = ({
  params,
  payload,
  eventId,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/student/events/${eventId}/register/`,
      params,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unregisterStudentFromEvent = ({
  eventId,
  params,
  data,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/student/events/${eventId}/unregister/`,
      params,
      data,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianEvents = ({ version = 'v1', params, timezone }) =>
  client
    .request({
      url: `${version}/event-scheduler/guardian/events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportCSVReports = ({
  timezone,
  params,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/events-and-contacts-csv/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getExternalOrgs = ({ timezone, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-applications/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const requestForEvent = ({ version = 'v1', params, payload }) =>
  client
    .request({
      url: `/${version}/higher-ed/k12-school-invite/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGuardianEventDetails = ({
  version = 'v1',
  params,
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/guardian/events/${eventId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadMemberEventFile = ({
  version = 'v1',
  eventId,
  timezone,
  params,
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/${eventId}/calendar-event/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
      params,
    })
    .then(handleFileResponse)
    .catch(handleError);

export const registerGuardianForEvent = ({
  eventId,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/guardian/events/${eventId}/register/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportStudentCSVReports = ({
  id,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/student-participants-csv/`,
      method: RequestMethod.POST,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const unregisterGuardianFromEvent = ({
  eventId,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/guardian/events/${eventId}/unregister/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportGuardianCSVReports = ({
  id,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/guardian-participants-csv/`,
      method: RequestMethod.POST,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getUpcomingMemberEvents = ({ version = 'v1', timezone, params }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberEventDetail = ({ version = 'v1', eventId, timezone }) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-events/${eventId}/`,
      method: RequestMethod.GET,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberEventDrawerDetails = ({
  version = 'v1',
  eventId,
  timezone,
  params,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-events/${eventId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEventRegisteredGuardians = ({
  params,
  eventId,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/student/events/${eventId}/registered-guardians/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictEvents = ({ params, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/districts/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictEventDetails = ({
  districtId,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/districts/${districtId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolEvents = ({ params, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/k12-schools/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolEventDetails = ({
  schoolId,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/k12-schools/${schoolId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStates = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/states/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAvailableDates = ({ params, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/dates/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const markStudentEventAsViewed = ({
  timezone,
  eventId,
  version = 'v1',
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/student/events/${eventId}/mark-as-viewed/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const markGuardianEventAsViewed = ({
  timezone,
  eventId,
  version = 'v1',
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/guardian/events/${eventId}/mark-as-viewed/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgEventCounts = ({ version = 'v1', params }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-events/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEventParticipants = ({ id, version = 'v1', params }) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/student-participants/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentEventParticipantsHash = ({
  id,
  version = 'v1',
  params,
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/${id}/student-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportStudentRegistrationReports = ({
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/k12-admin/events/events-and-students-csv/`,
      method: RequestMethod.POST,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganizationInstructionFiles = ({
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/confirmed-org-instruction-files/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventAttendeesHash = ({ version = 'v1', eventId, params }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${eventId}/student-attendees-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const uploadInstructionFile = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/confirmed-org-instruction-files/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteInstructionFile = ({ key, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/confirmed-org-instruction-files/${key}/delete/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteInstructions = ({ payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/confirmed-org-instruction-files/delete-instructions/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addStudentAttendees = ({
  version = 'v1',
  eventId,
  data,
  params,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${eventId}/student-attendees-add/`,
      method: RequestMethod.POST,
      data,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveEventEmails = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-emails/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEventEmail = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-emails/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 *
 * @param {string} hashId hashId of the updated event email
 * @returns
 */
export const updateEventEmail = ({ version = 'v1', hashId, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-emails/${hashId}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 *
 * @param {string} hashId hashId of the deleted event email
 * @returns
 */
export const deleteEventEmail = ({ version = 'v1', hashId, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-emails/${hashId}/delete/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventSurveyList = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-surveys/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventTemplateSurveyList = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-template-surveys/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEventSurvey = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-surveys/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEventTemplateSurvey = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-template-surveys/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventSurvey = ({ version = 'v1', hashId, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-surveys/${hashId}/`,
      method: RequestMethod.DELETE,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventTemplateSurveys = ({
  version = 'v1',
  hashId,
  payload,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/event-template-surveys/${hashId}/`,
      method: RequestMethod.DELETE,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentRegistrationCount = ({ version = 'v1', id }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${id}/students-count/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventsSurveyAnalytics = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/survey-analytics/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAttendingOrganizations = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/event-scheduler/event-applications/organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminPartnerEvents = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminPartnerEventDetails = ({
  id,
  timezone,
  version = 'v1',
  params = {},
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/${id}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminPartnerEventsCount = ({
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportPartnerEventCSVReports = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/events-and-contacts-csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportPartnerEventStudentCSVReports = ({
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/events-and-students-csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgHostedEvents = ({ params, timezone, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOrgHostedEvent = ({
  payload,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/`,
      method: RequestMethod.POST,
      data: payload,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteOrgHostedEvent = ({
  id,
  params,
  version = 'v1',
  payload,
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${id}/delete-event/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgHostedEventDetails = ({
  eventId,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${eventId}/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateOrgHostedEvent = ({
  eventId,
  payload,
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${eventId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrgHostedEventsCount = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventStudentParticipants = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-student-participants/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventGuardianParticipants = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-guardian-participants/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventK12AdminParticipants = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-k12admin-participants/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventDistrictParticipants = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-district-participants/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventStudentParticipantCount = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-student-participants/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventGuardianParticipantCount = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-guardian-participants/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventK12AdminParticipantCount = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-k12admin-participants/count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventDistrictParticipantCounts = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-district-participants/district-count/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventPartners = ({ params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const registerStaffToPartnerEvent = ({
  eventId,
  version = 'v1',
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/${eventId}/register/`,
      method: RequestMethod.POST,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unregisterStaffToPartnerEvent = ({
  eventId,
  payload,
  timezone,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/${eventId}/unregister/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventSchedulerDistrictSettings = ({
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/event-scheduler/district-setting/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventParticipants = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-participants/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHostEventParticipantsCount = ({
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/event-participants/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const sendEmailToEventContacts = ({
  eventId,
  payload,
  version = 'v1',
  params,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-events/${eventId}/send-email-to-contacts/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const downloadPartnerEventFile = ({
  version = 'v1',
  eventId,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events/${eventId}/calendar-event/`,
      method: RequestMethod.GET,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const createEventFlyers = ({ version = 'v1', timezone, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-flyers/`,
      method: RequestMethod.POST,
      data: payload,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventFlyers = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-flyers/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventFlyers = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-flyers/delete-event-flyers/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEventFlyer = ({ key, version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/event-flyers/${key}/delete/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AdminParticipants = ({ version = 'v1', params, id }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${id}/get_k12_admin_participants/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportK12AdminParticipantsCSV = ({
  version = 'v1',
  id,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${id}/k12-admin-participants-csv/`,
      method: RequestMethod.POST,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const exportK12AdminRegistrationCSV = ({
  version = 'v1',
  params,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/events-and-k12-admins-csv/`,
      method: RequestMethod.POST,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const registerK12AdminToEvent = ({ version = 'v1', id }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${id}/register/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unregisterK12AdminFromEvent = ({ version = 'v1', id, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${id}/unregister/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const registerStudentsToEvent = ({ version = 'v1', eventId, payload }) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events/${eventId}/register-students/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventDistricts = ({ version = 'v1', eventId, params }) =>
  client
    .request({
      url: `${version}/event-scheduler/events/${eventId}/districts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportHostEventStaffCSVReports = ({
  id,
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${id}/k12-admin-participants-csv/`,
      method: RequestMethod.POST,
      params,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const exportHostEventGuardianCSVReports = ({
  id,
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${id}/guardian-participants-csv/`,
      method: RequestMethod.POST,
      params,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const exportHostEventStudentCSVReports = ({
  id,
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${id}/student-participants-csv/`,
      method: RequestMethod.POST,
      params,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const getOrganizationEventsByMonth = ({
  version = 'v1',
  params,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-events-calendar/month-events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganizationEventsByWeekAndDay = ({
  version = 'v1',
  params,
  timezone,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-events-calendar/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportHostEventAllCSVReports = ({
  id,
  timezone,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/organization-hosted-events/${id}/all-participants-csv/`,
      method: RequestMethod.POST,
      params,
      responseType: 'blob',
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleFileResponse)
    .catch(handleError);

export const searchStudentsByEmails = ({
  params = {},
  version = 'v1',
  payload,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/search-students/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateHostEventStudentAttendees = ({
  eventId,
  params = {},
  version = 'v1',
  payload,
}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/organization-hosted-events/${eventId}/student-attendees-add/`,
      method: RequestMethod.POST,
      params,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventContactList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/event-contacts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStaffAvailabilities = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/meetings/availability-templates/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStaffAvailabilitySlots = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/meetings/availability-slots/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createMeetingWithCounselor = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/meetings/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const respondInvitation = ({ payload, id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/meetings/${id}/respond/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStaffEventsByMonth = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events-calendar/month-events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStaffEventsByWeekAndDay = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/events-calendar/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPartnerEventsByMonth = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events-calendar/month-events/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPartnerEventsByWeekAndDay = ({
  params,
  timezone,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/k12-admin/partner-events-calendar/`,
      method: RequestMethod.GET,
      params,
      headers: {
        [TIMEZONE_KEY]: timezone,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEventTerritoriesStats = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/event-scheduler/member/event-territories/statistics/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createTerritory = ({ payload, orgId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/?organization=${orgId}`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateTerritory = ({
  orgId,
  payload,
  territoryId,
  version = 'v1',
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/${territoryId}/?organization=${orgId}`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteTerritory = ({ orgId, territoryId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/${territoryId}/?organization=${orgId}`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTerritories = ({ orgId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/?organization=${orgId}`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getTerritoryType = ({ orgId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/type/?organization=${orgId}`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const confirmTerritory = ({ orgId, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/confirm-territory/?organization=${orgId}`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addDistrictOrSchoolToTerritory = ({
  orgId,
  payload,
  version = 'v1',
}) =>
  client
    .request({
      url: `/${version}/event-scheduler/member/event-territories/add-district-or-school/?organization=${orgId}`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);
